/**
 * HelpArticle — Full article view with TOC sidebar, breadcrumb, feedback, related articles
 */

import { h } from 'preact';
import { useState, useEffect, useMemo } from 'preact/hooks';
import { api } from '../api/client';
import { Button, ListSkeleton, SkeletonBar } from '../components/ui';
import { setMetaTags, clearMetaTags, setCanonical, clearCanonical, setJsonLd, clearJsonLd, buildCanonicalUrl, buildBreadcrumbLd } from '../lib/seo';

import { T } from '../styles/tokens';
const SUPPORTED_LOCALES = [
  'pt-BR', 'en', 'es', 'fr', 'de', 'it', 'ja', 'ko', 'zh-CN', 'zh-TW', 'ru',
  'ar', 'hi', 'nl', 'pl', 'tr', 'sv', 'da', 'nb', 'fi',
];

function HreflangTags({ path }: { path: string }) {
  useEffect(() => {
    document.querySelectorAll('link[data-hreflang]').forEach((el) => el.remove());
    const base = '/help';
    for (const loc of SUPPORTED_LOCALES) {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = loc;
      link.href = `${base}/${loc}${path}`;
      link.setAttribute('data-hreflang', 'true');
      document.head.appendChild(link);
    }
    const xd = document.createElement('link');
    xd.rel = 'alternate';
    xd.hreflang = 'x-default';
    xd.href = `${base}/en${path}`;
    xd.setAttribute('data-hreflang', 'true');
    document.head.appendChild(xd);
    return () => {
      document.querySelectorAll('link[data-hreflang]').forEach((el) => el.remove());
    };
  }, [path]);
  return null;
}

function renderMarkdown(md: string): string {
  return md
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/```(\w*)\n([\s\S]*?)```/g, `<pre style="background:${T.bg};border:1px solid ${T.border};padding:14px;border-radius:8px;overflow-x:auto;font-size:13px;margin:12px 0;line-height:1.6;font-family:monospace"><code>$2</code></pre>`)
    .replace(/`([^`]+)`/g, `<code style="background:${T.border};padding:2px 6px;border-radius:4px;font-size:13px">$1</code>`)
    .replace(/^### (.+)$/gm, (_m, t) => `<h3 id="${slugify(t)}" style="font-size:16px;font-weight:600;color:${T.text};margin:24px 0 8px">${t}</h3>`)
    .replace(/^## (.+)$/gm, (_m, t) => `<h2 id="${slugify(t)}" style="font-size:20px;font-weight:600;color:${T.text};margin:32px 0 12px;padding-bottom:8px;border-bottom:1px solid ${T.border}">${t}</h2>`)
    .replace(/^# (.+)$/gm, (_m, t) => `<h1 id="${slugify(t)}" style="font-size:24px;font-weight:700;color:${T.text};margin:0 0 16px">${t}</h1>`)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color:#5B8DEF;text-decoration:none" target="_blank">$1</a>')
    .replace(/^- (.+)$/gm, '<div style="margin-left:16px;padding:3px 0;display:flex;gap:8px"><span style="color:#5B8DEF">-</span><span>$1</span></div>')
    .replace(/^\d+\. (.+)$/gm, '<div style="margin-left:16px;padding:3px 0">$1</div>')
    .replace(/\n\n/g, '<div style="height:12px"></div>')
    .replace(/\n/g, '<br>');
}

function slugify(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function extractHeadings(md: string): { level: number; text: string; id: string }[] {
  const headings: { level: number; text: string; id: string }[] = [];
  const regex = /^(#{2,3}) (.+)$/gm;
  let match;
  while ((match = regex.exec(md)) !== null) {
    headings.push({ level: match[1].length, text: match[2], id: slugify(match[2]) });
  }
  return headings;
}

export function HelpArticlePage({ categorySlug, articleSlug, locale: propLocale }: { path?: string; categorySlug?: string; articleSlug?: string; locale?: string }) {
  const locale = propLocale || 'pt-BR';
  const isHelpDomain = typeof window !== 'undefined' && window.location.hostname.startsWith('help.');
  const basePrefix = isHelpDomain ? '' : '/help';
  const [article, setArticle] = useState<any>(null);
  const [category, setCategory] = useState<any>(null);
  const [related, setRelated] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedbackSent, setFeedbackSent] = useState(false);

  useEffect(() => {
    if (!articleSlug) return;
    api.get(`/cms/entries/${articleSlug}?contentType=help&locale=${locale}`).then((res: any) => {
      const entry = res?.entry || null;
      // Map CMS entry fields to article shape
      if (entry) {
        entry.content = entry.body || entry.content || '';
        entry.author = entry.author_name || entry.author || '';
      }
      setArticle(entry);
      // Category info is embedded in entry from CMS
      setCategory(entry ? { id: entry.category_id, slug: entry.category_slug, name: entry.category_name, icon: entry.category_icon, color: entry.category_color } : null);
      setRelated(res?.related || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [articleSlug, locale]);

  // SEO: meta tags, canonical, JSON-LD
  useEffect(() => {
    if (!article || !categorySlug || !articleSlug) return;
    const path = `${categorySlug}/${articleSlug}`;
    const url = buildCanonicalUrl(locale, path);
    setMetaTags({
      title: `${article.title || articleSlug} - Help Center`,
      description: article.excerpt || `${article.title} - WhiteLabelClaw Help Center`,
      url,
      type: 'article',
    });
    setCanonical(url);
    setJsonLd([
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        datePublished: article.published_at || article.created_at,
        dateModified: article.updated_at,
        author: { '@type': 'Organization', name: tenantBranding.value?.name || '' },
        publisher: { '@type': 'Organization', name: tenantBranding.value?.name || '' },
      },
      buildBreadcrumbLd([
        { name: 'Help Center', url: `/help/${locale}` },
        { name: category?.name || categorySlug, url: `/help/${locale}/${categorySlug}` },
        { name: article.title || articleSlug, url },
      ]),
    ]);
    return () => { clearMetaTags(); clearCanonical(); clearJsonLd(); };
  }, [article, category, categorySlug, articleSlug, locale]);

  const headings = useMemo(() => article?.content ? extractHeadings(article.content) : [], [article?.content]);
  const renderedContent = useMemo(() => article?.content ? renderMarkdown(article.content) : '', [article?.content]);

  const sendFeedback = async (helpful: boolean) => {
    if (feedbackSent || !article) return;
    setFeedbackSent(true);
    api.post(`/cms/entries/${article.id}/track`, { event_type: helpful ? 'click' : 'view', locale }).catch(() => {});
  };

  const formatDate = (d: string) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString(locale === 'en' ? 'en-US' : 'pt-BR', { year: 'numeric', month: 'long', day: 'numeric' }); } catch { return d; }
  };

  if (loading) return <div style={{ minHeight: '100vh', background: T.bg, padding: '60px 24px', maxWidth: 700, margin: '0 auto' }}><ListSkeleton rows={5} /></div>;
  if (!article) return <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.textMuted }}>Artigo nao encontrado</div>;

  return (
    <div style={{ minHeight: '100vh', background: T.bg, color: T.text, fontFamily: "'Inter', -apple-system, sans-serif" }}>
      <HreflangTags path={`/${categorySlug || ''}/${articleSlug || ''}`} />
      {/* Top bar */}
      <div style={{ padding: '16px 24px', borderBottom: `1px solid ${T.border}` }}>
        <a href={`${basePrefix}/${locale}/${categorySlug}`} style={{ color: T.primary, textDecoration: 'none', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          {category?.name || categorySlug}
        </a>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 20px 60px', display: 'flex', gap: 40 }}>
        {/* Main content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Breadcrumb */}
          <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 24, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' as const }}>
            <a href={`${basePrefix}/${locale}`} style={{ color: T.primary, textDecoration: 'none' }}>Help Center</a>
            <span>/</span>
            <a href={`${basePrefix}/${locale}/${categorySlug}`} style={{ color: T.primary, textDecoration: 'none' }}>{category?.name || categorySlug}</a>
            <span>/</span>
            <span style={{ color: T.textSecondary }}>{article.title}</span>
          </div>

          {/* Title */}
          <h1 style={{ fontSize: 28, fontWeight: 700, margin: '0 0 12px', lineHeight: 1.3 }}>{article.title}</h1>

          {/* Meta */}
          <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 32, display: 'flex', gap: 16, flexWrap: 'wrap' as const }}>
            <span>{locale === 'en' ? 'Updated' : 'Atualizado em'} {formatDate(article.updated_at)}</span>
            {article.author && <span>por {article.author}</span>}
          </div>

          {/* Content */}
          <div style={{ fontSize: 15, lineHeight: 1.7, color: T.textSecondary }} dangerouslySetInnerHTML={{ __html: renderedContent }} />

          {/* Feedback */}
          <div style={{ marginTop: 48, padding: 24, background: T.bgCard, borderRadius: 12, border: `1px solid ${T.border}`, textAlign: 'center' as const }}>
            {feedbackSent ? (
              <div style={{ color: T.primary, fontSize: 14, fontWeight: 500 }}>
                {locale === 'en' ? 'Thanks for your feedback!' : 'Obrigado pelo seu feedback!'}
              </div>
            ) : (
              <>
                <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 12 }}>
                  {locale === 'en' ? 'Was this article helpful?' : 'Este artigo foi util?'}
                </div>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                  <Button variant="ghost" size="sm" onClick={() => sendFeedback(true)}>
                    {locale === 'en' ? 'Yes' : 'Sim'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => sendFeedback(false)}>
                    {locale === 'en' ? 'No' : 'Nao'}
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Related articles */}
          {related.length > 0 && (
            <div style={{ marginTop: 40 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>{locale === 'en' ? 'Related articles' : 'Artigos relacionados'}</h3>
              {related.map((r: any) => (
                <a key={r.slug} href={`${basePrefix}/${locale}/${categorySlug}/${r.slug}`} style={{
                  display: 'block', padding: '10px 0', color: T.primary, textDecoration: 'none',
                  fontSize: 14, borderBottom: `1px solid ${T.borderSubtle}`,
                }}>{r.title}</a>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar TOC (desktop only) */}
        {headings.length > 0 && (
          <div style={{ width: 220, flexShrink: 0, position: 'sticky' as const, top: 80, alignSelf: 'flex-start' as const, display: window.innerWidth < 768 ? 'none' : 'block' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase' as const, letterSpacing: '0.5px', marginBottom: 12 }}>
              {locale === 'en' ? 'On this page' : 'Nesta pagina'}
            </div>
            {headings.map((h) => (
              <a key={h.id} href={`#${h.id}`} style={{
                display: 'block', fontSize: 13, color: T.textSecondary, textDecoration: 'none',
                padding: '4px 0', paddingLeft: h.level === 3 ? 12 : 0,
                transition: 'color 0.15s',
              }}
                onMouseEnter={(e: any) => e.currentTarget.style.color = T.primary}
                onMouseLeave={(e: any) => e.currentTarget.style.color = T.textSecondary}>
                {h.text}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
